<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';

import Button from 'dashboard/components-next/button/Button.vue';
import DealCard from './DealCard.vue';

const props = defineProps({
  stage: { type: Object, required: true },
  deals: { type: Array, default: () => [] },
});

const emit = defineEmits(['move', 'addDeal', 'open']);

const { t, locale } = useI18n();

const columnTotal = computed(() => {
  const cents = props.deals.reduce(
    (sum, deal) => sum + (deal.amountCents || 0),
    0
  );
  return new Intl.NumberFormat(locale.value, {
    style: 'currency',
    currency: props.deals[0]?.currency || 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100);
});

// vuedraggable segnala `added` sulla colonna di destinazione e `moved` sul riordino
// interno: in entrambi i casi la posizione finale e' quella che ci serve.
const onChange = event => {
  const change = event.added || event.moved;
  if (!change) return;

  emit('move', {
    dealId: change.element.id,
    stageId: props.stage.id,
    index: change.newIndex,
  });
};
</script>

<template>
  <div class="flex flex-col w-72 shrink-0 gap-3">
    <div class="flex items-center justify-between gap-2 px-1">
      <div class="flex items-center gap-2 min-w-0">
        <span
          class="rounded-full size-2 shrink-0"
          :style="{ backgroundColor: stage.color }"
        />
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ stage.name }}
        </span>
        <span class="text-xs text-n-slate-10">{{ deals.length }}</span>
      </div>
      <span class="text-xs font-medium shrink-0 text-n-slate-11">
        {{ columnTotal }}
      </span>
    </div>

    <Draggable
      :model-value="deals"
      :group="{ name: 'deals' }"
      item-key="id"
      class="flex flex-col gap-2 p-2 rounded-xl min-h-24 grow bg-n-alpha-1"
      ghost-class="opacity-40"
      @change="onChange"
    >
      <template #item="{ element }">
        <DealCard :deal="element" @open="emit('open', $event)" />
      </template>
    </Draggable>

    <Button
      :label="t('DEALS.BOARD.ADD_DEAL')"
      variant="ghost"
      size="sm"
      icon="i-lucide-plus"
      class="justify-start"
      @click="emit('addDeal', stage)"
    />
  </div>
</template>
